import { withWindow } from '@shared/utils/withWindow';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

/**
 * To detect text is overflowing with line clamp
 * @param maxLinesToShow - Show text max number of lines with -webkit-line-clamp
 *
 */
export function useTextLineClampOverflow(maxLinesToShow: number) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const frameIdRef = useRef<number | null>(null);

  const checkOverflow = useCallback(() => {
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
    }

    frameIdRef.current = requestAnimationFrame(() => {
      if (textRef.current) {
        // Calculate the maximum height of the collapsed element
        const maxHeight = parseFloat(getComputedStyle(textRef.current).lineHeight) * maxLinesToShow;
        setIsOverflowing(textRef.current?.scrollHeight > maxHeight);
      }

      frameIdRef.current = null;
    });
  }, [maxLinesToShow]);

  useEffect(() => {
    const textEl = textRef.current;
    if (!textEl) return;

    return withWindow(global => {
      global.addEventListener('resize', checkOverflow);
      return () => {
        global.removeEventListener('resize', checkOverflow);
      };
    });
  }, [checkOverflow]);

  useEffect(() => {
    const textEl = textRef.current;
    if (!textEl) return;

    const resizeObserver = createSafeResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(textEl, {});

    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }

      resizeObserver.disconnect();
    };
  }, [checkOverflow]);

  return { textRef, isOverflowing, checkOverflow };
}
